import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const TourForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    route: '',
    duration: '',
    itinerary: [
      { day: '1 день', description: '' }
    ],
    included: [],
    excursions: [],
    mark: '',
    image_url: '',
    gallery_images: [],
    status: 'active',
    slug: ''
  });

  const [newIncluded, setNewIncluded] = useState('');
  const [newExcursion, setNewExcursion] = useState('');
  const [editingIncludedIndex, setEditingIncludedIndex] = useState(null);
  const [editingIncludedText, setEditingIncludedText] = useState('');
  const [editingExcursionIndex, setEditingExcursionIndex] = useState(null);
  const [editingExcursionText, setEditingExcursionText] = useState('');
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);

  const parseDetailedDescription = (html) => {
    const result = {
      route: '',
      duration: '',
      itinerary: [],
      included: [],
      excursions: []
    };

    if (!html) return result;

    const routeMatch = html.match(/<p style="text-align: center;">([^<]+?)<\/p>/g);
    if (routeMatch) {
      if (routeMatch[0]) result.route = routeMatch[0].replace(/<[^>]*>/g, '');
      if (routeMatch[1]) result.duration = routeMatch[1].replace(/<[^>]*>/g, '');
    }

    const tableMatch = html.match(/<tbody>(.*?)<\/tbody>/s);
    if (tableMatch) {
      const rows = tableMatch[1].match(/<tr[^>]*>(.*?)<\/tr>/gs);
      if (rows) {
        rows.forEach(row => {
          const cells = row.match(/<td[^>]*>(.*?)<\/td>/gs);
          if (cells && cells.length >= 2) {
            const day = cells[0].replace(/<[^>]*>/g, '');
            const description = cells[1].replace(/<[^>]*>/g, '');
            result.itinerary.push({ day, description });
          }
        });
      }
    }

    const includedMatch = html.match(/<strong>В стоимость тура включено:<\/strong><br>(.*?)(?=<strong>|$)/s);
    if (includedMatch) {
      const items = includedMatch[1].match(/- ([^<]+)/g);
      if (items) {
        result.included = items.map(item => item.replace('- ', '').trim());
      }
    }

    const excursionsMatch = html.match(/<strong>Экскурсии:<\/strong><br>(.*?)(?=<p|$)/s);
    if (excursionsMatch) {
      const items = excursionsMatch[1].match(/- ([^<]+)/g);
      if (items) {
        result.excursions = items.map(item => item.replace('- ', '').trim());
      }
    }

    return result;
  };

  const generateDetailedDescription = () => {
    let html = '';
    html += `<p style="text-align: center;"><strong>${formData.title}</strong></p>`;
    if (formData.route) html += `<p style="text-align: center;">${formData.route}</p>`;
    if (formData.duration) html += `<p style="text-align: center;">${formData.duration}</p>`;
    
    if (formData.itinerary && formData.itinerary.length > 0) {
      html += `<div class="quill-table__wrapper"><table><colgroup><col width="50%"><col width="50%"></colgroup><tbody>`;
      formData.itinerary.forEach(item => {
        html += `<tr><td>${item.day}</td><td>${item.description}</td></tr>`;
      });
      html += `</tbody></table></div><br><br>`;
    }
    
    if (formData.included && formData.included.length > 0) {
      html += `<strong>В стоимость тура включено:</strong><br>`;
      formData.included.forEach(item => {
        html += `- ${item}<br>`;
      });
      html += `<br>`;
    }
    
    if (formData.excursions && formData.excursions.length > 0) {
      html += `<strong>Экскурсии:</strong><br>`;
      formData.excursions.forEach(item => {
        html += `- ${item}<br>`;
      });
    }
    
    return html;
  };

  const fetchTour = useCallback(async () => {
    if (!id) return;
    const { data, error } = await supabase
      .from('tours')
      .select('*')
      .eq('id', id)
      .single();
    
    if (!error && data) {
      if (data.detailed_description) {
        const parsed = parseDetailedDescription(data.detailed_description);
        setFormData({
          ...data,
          ...parsed
        });
      } else {
        setFormData(data);
      }
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchTour();
    }
  }, [id, fetchTour]);
  
  // ImagePlaceholder component
  const ImagePlaceholder = ({ text, width, height }) => (
    <div style={{
      width: width || '128px',
      height: height || '128px',
      backgroundColor: '#f3f4f6',
      borderRadius: '0.375rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: '0.5rem',
      fontSize: '12px',
      color: '#9ca3af'
    }}>
      <span style={{ fontSize: '24px' }}>🖼️</span>
      <span>{text || 'Нет изображения'}</span>
    </div>
  );

  const uploadImage = async (file, folder) => {
    // Проверяем файл
    if (!file) throw new Error('Файл не выбран');
    
    // Проверяем размер (5MB)
    if (file.size > 5 * 1024 * 1024) {
      throw new Error('Файл слишком большой. Максимальный размер 5MB');
    }
    
    // Проверяем тип
    if (!file.type.startsWith('image/')) {
      throw new Error('Пожалуйста, выберите изображение');
    }
    
    // Очищаем имя файла
    const cleanFileName = file.name.replace(/[^a-zA-Z0-9а-яА-Я._-]/g, '_');
    const fileName = `${Date.now()}_${cleanFileName}`;
    const filePath = `${folder}/${fileName}`;
    
    console.log('Uploading to bucket:', 'tour-images');
    console.log('File path:', filePath);
    console.log('File name:', file.name);
    console.log('File size:', file.size, 'bytes');
    
    // Пробуем загрузить без проверки бакета
    const { data, error } = await supabase.storage
      .from('tour-images')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type
      });
    
    if (error) {
      console.error('Upload error details:', error);
      // Если ошибка связана с тем, что бакет не существует, показываем понятное сообщение
      if (error.message.includes('bucket') || error.message.includes('not found')) {
        throw new Error('Бакет "tour-images" не найден. Пожалуйста, создайте его в Supabase Storage.');
      }
      throw new Error(`Ошибка загрузки: ${error.message}`);
    }
    
    console.log('Upload success:', data);
    
    // Получаем публичный URL
    const { data: { publicUrl } } = supabase.storage
      .from('tour-images')
      .getPublicUrl(filePath);
    
    console.log('Public URL:', publicUrl);
    return publicUrl;
  };
  
  const handleMainImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    setUploadProgress('Загрузка главного изображения...');
    
    try {
      const url = await uploadImage(file, 'main');
      setFormData(prev => ({ ...prev, image_url: url }));
      setUploadProgress('');
      alert('Изображение успешно загружено!');
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Ошибка при загрузке изображения: ' + error.message);
      setUploadProgress('');
    } finally {
      setUploading(false);
    }
  };

  const handleMainImageUrl = (url) => {
    if (url.trim()) {
      setFormData(prev => ({ ...prev, image_url: url.trim() }));
    }
  };

  const handleGalleryImagesChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    setUploading(true);
    const urls = [];
    let successCount = 0;
    let errorCount = 0;
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress(`Загрузка изображения ${i + 1} из ${files.length}...`);
      
      try {
        const url = await uploadImage(file, 'gallery');
        urls.push(url);
        successCount++;
      } catch (error) {
        console.error('Error uploading gallery image:', error);
        errorCount++;
        alert(`Ошибка при загрузке изображения "${file.name}": ${error.message}`);
      }
    }
    
    if (urls.length > 0) {
      setFormData(prev => ({
        ...prev,
        gallery_images: [...prev.gallery_images, ...urls]
      }));
      alert(`Загружено ${successCount} из ${files.length} изображений${errorCount > 0 ? `. Ошибок: ${errorCount}` : ''}`);
    }
    
    setUploadProgress('');
    setUploading(false);
  };

  const handleGalleryImageUrl = (url) => {
    if (url.trim()) {
      setFormData(prev => ({
        ...prev,
        gallery_images: [...prev.gallery_images, url.trim()]
      }));
    }
  };

  const removeGalleryImage = (index) => {
    setFormData(prev => ({
      ...prev,
      gallery_images: prev.gallery_images.filter((_, i) => i !== index)
    }));
  };

  // Drag and drop handlers для included списка
  const handleDragStart = (index) => {
    setDraggedItemIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItemIndex === null) return;
    if (draggedItemIndex === index) return;
    
    const newIncluded = [...formData.included];
    const draggedItem = newIncluded[draggedItemIndex];
    newIncluded.splice(draggedItemIndex, 1);
    newIncluded.splice(index, 0, draggedItem);
    
    setFormData(prev => ({ ...prev, included: newIncluded }));
    setDraggedItemIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedItemIndex(null);
  };

  // Drag and drop handlers для excursions списка
  const handleExcursionDragStart = (index) => {
    setDraggedItemIndex(index);
  };

  const handleExcursionDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItemIndex === null) return;
    if (draggedItemIndex === index) return;
    
    const newExcursions = [...formData.excursions];
    const draggedItem = newExcursions[draggedItemIndex];
    newExcursions.splice(draggedItemIndex, 1);
    newExcursions.splice(index, 0, draggedItem);
    
    setFormData(prev => ({ ...prev, excursions: newExcursions }));
    setDraggedItemIndex(index);
  };

  const handleExcursionDragEnd = () => {
    setDraggedItemIndex(null);
  };

  // Редактирование элемента included
  const startEditIncluded = (index, text) => {
    setEditingIncludedIndex(index);
    setEditingIncludedText(text);
  };

  const saveEditIncluded = (index) => {
    const newIncluded = [...formData.included];
    newIncluded[index] = editingIncludedText;
    setFormData(prev => ({ ...prev, included: newIncluded }));
    setEditingIncludedIndex(null);
    setEditingIncludedText('');
  };

  const cancelEditIncluded = () => {
    setEditingIncludedIndex(null);
    setEditingIncludedText('');
  };

  // Редактирование элемента excursion
  const startEditExcursion = (index, text) => {
    setEditingExcursionIndex(index);
    setEditingExcursionText(text);
  };

  const saveEditExcursion = (index) => {
    const newExcursions = [...formData.excursions];
    newExcursions[index] = editingExcursionText;
    setFormData(prev => ({ ...prev, excursions: newExcursions }));
    setEditingExcursionIndex(null);
    setEditingExcursionText('');
  };

  const cancelEditExcursion = () => {
    setEditingExcursionIndex(null);
    setEditingExcursionText('');
  };

  const addIncluded = () => {
    if (newIncluded.trim()) {
      setFormData(prev => ({
        ...prev,
        included: [...prev.included, newIncluded.trim()]
      }));
      setNewIncluded('');
    }
  };

  const removeIncluded = (index) => {
    setFormData(prev => ({
      ...prev,
      included: prev.included.filter((_, i) => i !== index)
    }));
  };

  const addExcursion = () => {
    if (newExcursion.trim()) {
      setFormData(prev => ({
        ...prev,
        excursions: [...prev.excursions, newExcursion.trim()]
      }));
      setNewExcursion('');
    }
  };

  const removeExcursion = (index) => {
    setFormData(prev => ({
      ...prev,
      excursions: prev.excursions.filter((_, i) => i !== index)
    }));
  };

  const addItineraryDay = () => {
    setFormData(prev => ({
      ...prev,
      itinerary: [...prev.itinerary, { day: `${prev.itinerary.length + 1} день`, description: '' }]
    }));
  };

  const updateItinerary = (index, field, value) => {
    const updated = [...formData.itinerary];
    updated[index][field] = value;
    setFormData(prev => ({ ...prev, itinerary: updated }));
  };

  const removeItinerary = (index) => {
    setFormData(prev => ({
      ...prev,
      itinerary: prev.itinerary.filter((_, i) => i !== index)
    }));
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-zа-яё\s]/g, '')
      .replace(/\s+/g, '-')
      .replace(/[ё]/g, 'e');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const dataToSave = {
      title: formData.title,
      description: formData.description,
      detailed_description: generateDetailedDescription(),
      mark: formData.mark || null,
      image_url: formData.image_url || null,
      gallery_images: formData.gallery_images || [],
      status: formData.status,
      slug: formData.slug || generateSlug(formData.title)
    };
    
    let result;
    if (id) {
      result = await supabase
        .from('tours')
        .update(dataToSave)
        .eq('id', id);
    } else {
      result = await supabase
        .from('tours')
        .insert([dataToSave]);
    }
    
    if (!result.error) {
      navigate('/admin/tours');
    } else {
      alert('Ошибка при сохранении тура: ' + result.error.message);
    }
    setLoading(false);
  };

  return (
    <div>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>
        {id ? 'Редактировать тур' : 'Добавить тур'}
      </h1>
      
      {uploadProgress && (
        <div style={{
          backgroundColor: '#3b82f6',
          color: 'white',
          padding: '0.75rem',
          borderRadius: '0.375rem',
          marginBottom: '1rem',
          textAlign: 'center'
        }}>
          {uploadProgress}
        </div>
      )}
      
      <form onSubmit={handleSubmit} style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        padding: '1.5rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        {/* Основная информация */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Название тура *
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            style={{
              width: '100%',
              padding: '0.5rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem'
            }}
            required
          />
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Краткое описание (для карточки)
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={3}
            style={{
              width: '100%',
              padding: '0.5rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem'
            }}
          />
        </div>
        
        {/* Маршрут */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Маршрут (например: Владивосток-Краскино-Хуньчунь-Краскино-Владивосток)
          </label>
          <input
            type="text"
            value={formData.route}
            onChange={(e) => setFormData({ ...formData, route: e.target.value })}
            style={{
              width: '100%',
              padding: '0.5rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem'
            }}
          />
        </div>
        
        {/* Длительность */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Длительность (например: От 2-ух до 30-ти дней)
          </label>
          <input
            type="text"
            value={formData.duration}
            onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
            style={{
              width: '100%',
              padding: '0.5rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem'
            }}
          />
        </div>
        
        {/* Программа тура (таблица) */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Программа тура
          </label>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.375rem', padding: '1rem' }}>
            {formData.itinerary.map((item, index) => (
              <div key={index} style={{ marginBottom: '0.5rem', display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  value={item.day}
                  onChange={(e) => updateItinerary(index, 'day', e.target.value)}
                  placeholder="День"
                  style={{
                    width: '120px',
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem'
                  }}
                />
                <input
                  type="text"
                  value={item.description}
                  onChange={(e) => updateItinerary(index, 'description', e.target.value)}
                  placeholder="Описание дня"
                  style={{
                    flex: 1,
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem'
                  }}
                />
                <button
                  type="button"
                  onClick={() => removeItinerary(index)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: 'pointer'
                  }}
                >
                  Удалить
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addItineraryDay}
              style={{
                marginTop: '0.5rem',
                padding: '0.5rem 1rem',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: 'pointer'
              }}
            >
              + Добавить день
            </button>
          </div>
        </div>
        
        {/* Включено в стоимость - с drag-and-drop и редактированием */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Включено в стоимость (перетаскивайте элементы для изменения порядка)
          </label>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.375rem', padding: '1rem' }}>
            {formData.included.map((item, index) => (
              <div
                key={index}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                style={{
                  marginBottom: '0.5rem',
                  display: 'flex',
                  gap: '0.5rem',
                  alignItems: 'center',
                  cursor: 'grab',
                  backgroundColor: draggedItemIndex === index ? '#e5e7eb' : 'transparent',
                  padding: '0.5rem',
                  borderRadius: '0.375rem'
                }}
              >
                <span style={{ cursor: 'grab', fontSize: '1.2rem' }}>⋮⋮</span>
                {editingIncludedIndex === index ? (
                  <input
                    type="text"
                    value={editingIncludedText}
                    onChange={(e) => setEditingIncludedText(e.target.value)}
                    onBlur={() => saveEditIncluded(index)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveEditIncluded(index);
                      if (e.key === 'Escape') cancelEditIncluded();
                    }}
                    autoFocus
                    style={{
                      flex: 1,
                      padding: '0.5rem 0.75rem',
                      border: '1px solid #3b82f6',
                      borderRadius: '0.375rem',
                      backgroundColor: '#fff'
                    }}
                  />
                ) : (
                  <span
                    style={{ flex: 1, padding: '0.5rem 0.75rem', backgroundColor: '#f3f4f6', borderRadius: '0.375rem', cursor: 'pointer' }}
                    onDoubleClick={() => startEditIncluded(index, item)}
                  >
                    {item}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => removeIncluded(index)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: 'pointer'
                  }}
                >
                  Удалить
                </button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
              <input
                type="text"
                value={newIncluded}
                onChange={(e) => setNewIncluded(e.target.value)}
                placeholder="Например: Транспортное обслуживание"
                style={{
                  flex: 1,
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem'
                }}
              />
              <button
                type="button"
                onClick={addIncluded}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer'
                }}
              >
                Добавить
              </button>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
              💡 Двойной клик для редактирования, перетаскивание за ⋮⋮ для изменения порядка
            </div>
          </div>
        </div>
        
        {/* Экскурсии - с drag-and-drop и редактированием */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Экскурсии (перетаскивайте элементы для изменения порядка)
          </label>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.375rem', padding: '1rem' }}>
            {formData.excursions.map((item, index) => (
              <div
                key={index}
                draggable
                onDragStart={() => handleExcursionDragStart(index)}
                onDragOver={(e) => handleExcursionDragOver(e, index)}
                onDragEnd={handleExcursionDragEnd}
                style={{
                  marginBottom: '0.5rem',
                  display: 'flex',
                  gap: '0.5rem',
                  alignItems: 'center',
                  cursor: 'grab',
                  backgroundColor: draggedItemIndex === index ? '#e5e7eb' : 'transparent',
                  padding: '0.5rem',
                  borderRadius: '0.375rem'
                }}
              >
                <span style={{ cursor: 'grab', fontSize: '1.2rem' }}>⋮⋮</span>
                {editingExcursionIndex === index ? (
                  <input
                    type="text"
                    value={editingExcursionText}
                    onChange={(e) => setEditingExcursionText(e.target.value)}
                    onBlur={() => saveEditExcursion(index)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveEditExcursion(index);
                      if (e.key === 'Escape') cancelEditExcursion();
                    }}
                    autoFocus
                    style={{
                      flex: 1,
                      padding: '0.5rem 0.75rem',
                      border: '1px solid #3b82f6',
                      borderRadius: '0.375rem',
                      backgroundColor: '#fff'
                    }}
                  />
                ) : (
                  <span
                    style={{ flex: 1, padding: '0.5rem 0.75rem', backgroundColor: '#f3f4f6', borderRadius: '0.375rem', cursor: 'pointer' }}
                    onDoubleClick={() => startEditExcursion(index, item)}
                  >
                    {item}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => removeExcursion(index)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: 'pointer'
                  }}
                >
                  Удалить
                </button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
              <input
                type="text"
                value={newExcursion}
                onChange={(e) => setNewExcursion(e.target.value)}
                placeholder="Например: Обзорная экскурсия «Вечерний Хуньчунь» - 80 юаней"
                style={{
                  flex: 1,
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem'
                }}
              />
              <button
                type="button"
                onClick={addExcursion}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer'
                }}
              >
                Добавить
              </button>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
              💡 Двойной клик для редактирования, перетаскивание за ⋮⋮ для изменения порядка
            </div>
          </div>
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Метка (например, "Сентябрь")
          </label>
          <input
            type="text"
            value={formData.mark}
            onChange={(e) => setFormData({ ...formData, mark: e.target.value })}
            style={{
              width: '100%',
              padding: '0.5rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem'
            }}
          />
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Статус
          </label>
          <select
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            style={{
              width: '100%',
              padding: '0.5rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem'
            }}
          >
            <option value="active">Активен (показывать на сайте)</option>
            <option value="archived">В архиве (скрыть с сайта)</option>
          </select>
        </div>
        
        {/* Главное изображение */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Главное изображение
          </label>
          <div style={{
            border: '2px dashed #e5e7eb',
            borderRadius: '0.5rem',
            padding: '1rem'
          }}>
            {formData.image_url ? (
              <div style={{ textAlign: 'center', marginBottom: '1rem', position: 'relative' }}>
                <img
                  src={formData.image_url}
                  alt="Main"
                  style={{ width: '128px', height: '128px', objectFit: 'cover', margin: '0 auto', borderRadius: '0.375rem' }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) {
                      e.target.nextSibling.style.display = 'flex';
                    }
                  }}
                />
                <div style={{
                  display: 'none',
                  width: '128px',
                  height: '128px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '0.375rem',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  color: '#9ca3af',
                  margin: '0 auto'
                }}>
                  🖼️ Ошибка загрузки
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, image_url: '' })}
                  style={{
                    marginTop: '0.5rem',
                    padding: '0.25rem 0.5rem',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.25rem',
                    cursor: 'pointer'
                  }}
                >
                  Удалить
                </button>
              </div>
            ) : (
              <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                <ImagePlaceholder text="Нет изображения" width="128px" height="128px" />
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <input
                type="text"
                placeholder="Или вставьте URL изображения"
                id="mainImageUrl"
                style={{
                  flex: 1,
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem'
                }}
              />
              <button
                type="button"
                onClick={() => {
                  const url = document.getElementById('mainImageUrl').value;
                  if (url) handleMainImageUrl(url);
                  document.getElementById('mainImageUrl').value = '';
                }}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer'
                }}
              >
                Вставить URL
              </button>
            </div>
            
            <label style={{ cursor: 'pointer', display: 'block', textAlign: 'center', padding: '0.5rem', border: '1px dashed #d1d5db', borderRadius: '0.375rem', backgroundColor: '#f9fafb' }}>
              <input
                type="file"
                accept="image/*"
                onChange={handleMainImageChange}
                style={{ display: 'none' }}
                disabled={uploading}
              />
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '20px' }}>📁</span>
                {uploading && uploadProgress.includes('главного') ? 'Загрузка...' : 'Загрузить изображение с компьютера'}
              </span>
            </label>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem', textAlign: 'center' }}>
              Поддерживаемые форматы: JPG, PNG, GIF. Максимальный размер: 5MB
            </div>
          </div>
        </div>
        
        {/* Галерея изображений */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Галерея изображений
          </label>
          <div style={{
            border: '2px dashed #e5e7eb',
            borderRadius: '0.5rem',
            padding: '1rem'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', marginBottom: '1rem' }}>
              {formData.gallery_images.map((url, index) => (
                <div key={index} style={{ position: 'relative' }}>
                  <img
                    src={url}
                    alt={`Gallery ${index}`}
                    style={{ width: '100%', height: '80px', objectFit: 'cover', borderRadius: '0.375rem' }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) {
                        e.target.nextSibling.style.display = 'flex';
                      }
                    }}
                  />
                  <div style={{
                    display: 'none',
                    width: '100%',
                    height: '80px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '0.375rem',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    color: '#9ca3af'
                  }}>
                    🖼️ Ошибка загрузки
                  </div>
                  <button
                    type="button"
                    onClick={() => removeGalleryImage(index)}
                    style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '12px',
                      zIndex: 10
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <input
                type="text"
                placeholder="Введите URL изображения для галереи"
                id="galleryImageUrl"
                style={{
                  flex: 1,
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem'
                }}
              />
              <button
                type="button"
                onClick={() => {
                  const url = document.getElementById('galleryImageUrl').value;
                  if (url) handleGalleryImageUrl(url);
                  document.getElementById('galleryImageUrl').value = '';
                }}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer'
                }}
              >
                Добавить URL
              </button>
            </div>
            
            <label style={{ cursor: 'pointer', display: 'block', textAlign: 'center', padding: '0.5rem', border: '1px dashed #d1d5db', borderRadius: '0.375rem', backgroundColor: '#f9fafb' }}>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleGalleryImagesChange}
                style={{ display: 'none' }}
                disabled={uploading}
              />
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '20px' }}>🖼️</span>
                {uploading && uploadProgress.includes('галереи') ? 'Загрузка...' : 'Загрузить изображения с компьютера (можно несколько)'}
              </span>
            </label>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem', textAlign: 'center' }}>
              Поддерживаемые форматы: JPG, PNG, GIF. Максимальный размер каждого файла: 5MB
            </div>
          </div>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1.5rem' }}>
          <button
            type="button"
            onClick={() => navigate('/admin/tours')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#9ca3af',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer'
            }}
          >
            Отмена
          </button>
          <button
            type="submit"
            disabled={loading || uploading}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              opacity: (loading || uploading) ? 0.5 : 1
            }}
          >
            {loading ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TourForm;